import React, { useState, useEffect, useMemo } from 'react';
import { PageTransition } from '../ui/PageTransition';
import {
  Personagem,
  PersonagemFormData,
  PersonagemStatusFilter,
  PersonagemUsoFilter,
  PersonagemSortOption
} from '../../types/personagem';
import { Contract } from '../../types/contract';
import { personagemService } from '../../services/personagemService';
import { contractService } from '../../services/contractService';
import { getPersonagemStats, PersonagemStats } from '../../utils/personagemStats';
import { parseMoneyValue } from '../../utils/currencyUtils';
import { toMillisFlexible } from '../../utils/dateUtils';
import {
  PersonagensHeader,
  PersonagensGrid,
  PersonagensFilterBar,
  PersonagemFormModal,
  PersonagemDetailModal,
  PersonagemImageLightbox
} from './personagem-components';

const Personagens: React.FC = () => {
  const [personagens, setPersonagens] = useState<Personagem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<PersonagemStatusFilter>('todos');
  const [categoriaFilter, setCategoriaFilter] = useState('todas');
  const [usoFilter, setUsoFilter] = useState<PersonagemUsoFilter>('todos');
  const [precoMin, setPrecoMin] = useState('');
  const [precoMax, setPrecoMax] = useState('');
  const [sortOption, setSortOption] = useState<PersonagemSortOption>('nome-asc');
  const [groupByCategory, setGroupByCategory] = useState(false);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingPersonagem, setEditingPersonagem] = useState<Personagem | null>(null);

  const [detailPersonagem, setDetailPersonagem] = useState<Personagem | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(true);

  const loadPersonagens = async () => {
    try {
      setLoading(true);
      const lista = await personagemService.getPersonagens();
      setPersonagens(lista);
    } catch (error) {
      console.error('Erro ao carregar personagens:', error);
      alert('Erro ao carregar personagens. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const loadContracts = async () => {
    try {
      setLoadingContracts(true);
      const lista = await contractService.getContracts();
      setContracts(lista);
    } catch (error) {
      console.error('Erro ao carregar contratos para estatísticas dos personagens:', error);
    } finally {
      setLoadingContracts(false);
    }
  };

  useEffect(() => {
    loadPersonagens();
    loadContracts();
  }, []);

  // Estatísticas (contratos em que foi usado + receita) calculadas uma
  // única vez para todos os personagens, reaproveitadas pelos cards, pelo
  // modal de detalhes e pela ordenação/filtro por uso e receita.
  const statsById = useMemo(() => {
    const map = new Map<string, PersonagemStats>();
    personagens.forEach((personagem) => {
      map.set(personagem.id, getPersonagemStats(personagem.nome, contracts));
    });
    return map;
  }, [personagens, contracts]);

  const categorias = useMemo(() => {
    const set = new Set<string>();
    personagens.forEach((p) => {
      if (p.categoria?.trim()) set.add(p.categoria.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [personagens]);

  const filteredAndSortedPersonagens = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const min = precoMin ? parseFloat(precoMin) : null;
    const max = precoMax ? parseFloat(precoMax) : null;

    const filtered = personagens.filter((p) => {
      if (term && !p.nome.toLowerCase().includes(term) && !(p.categoria || '').toLowerCase().includes(term)) {
        return false;
      }
      if (statusFilter === 'ativo' && !p.ativo) return false;
      if (statusFilter === 'inativo' && p.ativo) return false;
      if (categoriaFilter !== 'todas' && (p.categoria || '').trim() !== categoriaFilter) return false;

      const preco = parseMoneyValue(p.precoBase);
      if (min !== null && (isNaN(preco) || preco < min)) return false;
      if (max !== null && (isNaN(preco) || preco > max)) return false;

      const stats = statsById.get(p.id);
      const usado = (stats?.totalEventos || 0) > 0;
      if (usoFilter === 'usados' && !usado) return false;
      if (usoFilter === 'nao-usados' && usado) return false;

      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      const statsA = statsById.get(a.id);
      const statsB = statsById.get(b.id);

      switch (sortOption) {
        case 'nome-desc':
          return b.nome.localeCompare(a.nome, 'pt-BR');
        case 'preco-desc':
          return (parseMoneyValue(b.precoBase) || 0) - (parseMoneyValue(a.precoBase) || 0);
        case 'preco-asc':
          return (parseMoneyValue(a.precoBase) || 0) - (parseMoneyValue(b.precoBase) || 0);
        case 'receita-desc':
          return (statsB?.receitaTotal || 0) - (statsA?.receitaTotal || 0);
        case 'receita-asc':
          return (statsA?.receitaTotal || 0) - (statsB?.receitaTotal || 0);
        case 'uso-desc':
          return (statsB?.totalEventos || 0) - (statsA?.totalEventos || 0);
        case 'uso-asc':
          return (statsA?.totalEventos || 0) - (statsB?.totalEventos || 0);
        case 'recente':
          return toMillisFlexible(b.createdAt) - toMillisFlexible(a.createdAt);
        case 'antigo':
          return toMillisFlexible(a.createdAt) - toMillisFlexible(b.createdAt);
        case 'nome-asc':
        default:
          return a.nome.localeCompare(b.nome, 'pt-BR');
      }
    });

    return sorted;
  }, [personagens, statsById, searchTerm, statusFilter, categoriaFilter, usoFilter, precoMin, precoMax, sortOption]);

  const handleClearFilters = () => {
    setStatusFilter('todos');
    setCategoriaFilter('todas');
    setUsoFilter('todos');
    setPrecoMin('');
    setPrecoMax('');
    setSortOption('nome-asc');
    setGroupByCategory(false);
  };

  const handleOpenAddModal = () => {
    setEditingPersonagem(null);
    setShowFormModal(true);
  };

  const handleOpenEditModal = (personagem: Personagem) => {
    setEditingPersonagem(personagem);
    setShowFormModal(true);
  };

  const handleCloseModal = () => {
    if (saving) return;
    setShowFormModal(false);
    setEditingPersonagem(null);
  };

  const handleSave = async (data: PersonagemFormData) => {
    setSaving(true);
    try {
      if (editingPersonagem) {
        await personagemService.updatePersonagem(editingPersonagem.id, data, editingPersonagem.fotoPath);
      } else {
        await personagemService.addPersonagem(data);
      }
      await loadPersonagens();
      setShowFormModal(false);
      setEditingPersonagem(null);
    } catch (error) {
      console.error('Erro ao salvar personagem:', error);
      alert('Erro ao salvar personagem. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (personagem: Personagem) => {
    const confirmed = window.confirm(`Excluir o personagem "${personagem.nome}"? Essa ação não pode ser desfeita.`);
    if (!confirmed) return;

    try {
      await personagemService.deletePersonagem(personagem.id, personagem.fotoPath);
      setPersonagens((prev) => prev.filter((p) => p.id !== personagem.id));
    } catch (error) {
      console.error('Erro ao excluir personagem:', error);
      alert('Erro ao excluir personagem. Tente novamente.');
    }
  };

  const handleToggleAtivo = async (personagem: Personagem) => {
    const novoStatus = !personagem.ativo;
    setPersonagens((prev) => prev.map((p) => (p.id === personagem.id ? { ...p, ativo: novoStatus } : p)));
    try {
      await personagemService.setAtivo(personagem.id, novoStatus);
    } catch (error) {
      console.error('Erro ao alterar status do personagem:', error);
      setPersonagens((prev) => prev.map((p) => (p.id === personagem.id ? { ...p, ativo: !novoStatus } : p)));
      alert('Erro ao alterar status do personagem. Tente novamente.');
    }
  };

  const handleOpenDetail = (personagem: Personagem) => {
    setDetailPersonagem(personagem);
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
            <p className="text-slate-600">Carregando personagens...</p>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="w-full mx-auto px-2 sm:px-4 lg:px-6 overflow-x-hidden space-y-4">
        <PersonagensHeader onAddPersonagem={handleOpenAddModal} />

        <PersonagensFilterBar
          categorias={categorias}
          statusFilter={statusFilter}
          categoriaFilter={categoriaFilter}
          usoFilter={usoFilter}
          precoMin={precoMin}
          precoMax={precoMax}
          sortOption={sortOption}
          groupByCategory={groupByCategory}
          resultCount={filteredAndSortedPersonagens.length}
          totalCount={personagens.length}
          onStatusChange={setStatusFilter}
          onCategoriaChange={setCategoriaFilter}
          onUsoChange={setUsoFilter}
          onPrecoMinChange={setPrecoMin}
          onPrecoMaxChange={setPrecoMax}
          onSortChange={setSortOption}
          onGroupByCategoryChange={setGroupByCategory}
          onClearFilters={handleClearFilters}
        />

        <div className="w-full">
          <PersonagensGrid
            personagens={filteredAndSortedPersonagens}
            statsById={statsById}
            loadingStats={loadingContracts}
            totalCadastrados={personagens.length}
            groupByCategory={groupByCategory}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onAddPersonagem={handleOpenAddModal}
            onOpenDetail={handleOpenDetail}
            onOpenImage={setLightboxImage}
            onEdit={handleOpenEditModal}
            onDelete={handleDelete}
            onToggleAtivo={handleToggleAtivo}
          />
        </div>

        <PersonagemFormModal
          isOpen={showFormModal}
          personagem={editingPersonagem}
          saving={saving}
          onClose={handleCloseModal}
          onSave={handleSave}
        />

        <PersonagemDetailModal
          personagem={detailPersonagem}
          stats={detailPersonagem ? statsById.get(detailPersonagem.id) || null : null}
          loadingStats={loadingContracts}
          onClose={() => setDetailPersonagem(null)}
          onImageClick={setLightboxImage}
        />

        <PersonagemImageLightbox
          imageUrl={lightboxImage}
          alt={detailPersonagem?.nome || 'Personagem'}
          onClose={() => setLightboxImage(null)}
        />
      </div>
    </PageTransition>
  );
};

export default Personagens;
